// Wiring: file input and drop zone -> worker -> runs -> clip -> viewer, with
// the options panel and the export dialog.

import type { Run } from './model.ts'
import { builtinSchemes } from './overlays/builtin/index.ts'
import { formatPercent, type CheckFocus, type CheckOverlay, type VisibleScheme } from './overlays/draw.ts'
import { attenuationDb, insertionLossDb, resolveFilter, type ResolvedFilter } from './overlays/filter.ts'
import { collisionProfile, evaluateCenter, scoreAt, type CenterScore, type CollisionProfile } from './overlays/fit.ts'
import { loadUserSchemes, saveUserSchemes, type Scheme } from './overlays/scheme.ts'
import { describeShape, signalShape, type PlacedSignal } from './overlays/signal.ts'
import type { LoadMessage, LoadRequest } from './parse/worker.ts'
import LoadWorker from './parse/worker.ts?worker'
import { DEFAULT_EXPORT_WIDTH, exportFileName, MAX_EXPORT_ROWS, planExport, renderExport } from './render/export.ts'
import { buildClip, clampDbRange, displayedBand } from './render/layout.ts'
import {
  defaultSettings,
  displayZone,
  effectiveCaptureZone,
  loadSettings,
  saveSettings,
  threshold,
  type Settings,
} from './state.ts'
import { formatClock } from './tz.ts'
import { formatHz, formatMHz } from './units.ts'
import { loadSource, readViewState, viewQuery, type ViewState } from './url.ts'
import { Controls } from './ui/controls.ts'
import { SchemePanel } from './ui/schemes.ts'
import { initSections } from './ui/sections.ts'
import { Viewer } from './ui/viewer.ts'

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id)
  if (!el) throw new Error(`missing #${id}`)
  return el as T
}

const status = $<HTMLSpanElement>('status')
const runSelect = $<HTMLSelectElement>('run')
const fileInput = $<HTMLInputElement>('file')
const exportButton = $<HTMLButtonElement>('export')
const resetViewButton = $<HTMLButtonElement>('reset-view')
const toggleControls = $<HTMLButtonElement>('toggle-controls')
const controlsPanel = $<HTMLElement>('controls')
const drop = $<HTMLElement>('drop')
const diagnostics = $<HTMLDetailsElement>('diagnostics')
const diagnosticsList = $<HTMLUListElement>('diagnostics-list')
const empty = $<HTMLElement>('empty')
const emptyTitle = $<HTMLParagraphElement>('empty-title')
const emptyNote = $<HTMLParagraphElement>('empty-note')
const emptyOpen = $<HTMLButtonElement>('empty-open')
const exportDialog = $<HTMLDialogElement>('export-dialog')
const exportInfo = $<HTMLParagraphElement>('export-info')
const exportWarning = $<HTMLParagraphElement>('export-warning')
const exportOk = $<HTMLButtonElement>('export-ok')

// A `?load=` link carries the view it was shared from. Its settings win over
// the saved ones: the point of the link is to show the sender's picture, not
// the recipient's options applied to the sender's capture.
let sharedSource: string | null = loadSource(location.search)
const sharedView: ViewState | null = sharedSource ? readViewState(location.search) : null

let settings: Settings = { ...loadSettings(), ...(sharedView?.settings ?? {}) }
settings.captureZone = effectiveCaptureZone(settings)
/** The shared window and block, held until a run exists to resolve them against. */
let pendingView: ViewState | null = sharedView
let runs: Run[] = []
let currentRun = 0
let fileName = ''

const viewer = new Viewer(
  {
    tape: $('tape'),
    stripsRow: $('strips-row'),
    strips: $('strips'),
    gutter: $('gutter'),
    heatWrap: $('heat-wrap'),
    heat: $('heat'),
    overlay: $('overlay'),
    tooltip: $('tooltip'),
    footer: $('footer'),
  },
  {
    palette: settings.palette,
    displayZone: displayZone(settings),
    ytickSec: settings.ytickSec,
    dbMin: 0,
    dbMax: 1,
    schemes: [],
    check: null,
    pins: [],
    filter: null,
  },
)

const controls = new Controls($<HTMLFormElement>('options'), (patch) => {
  settings = { ...settings, ...patch }
  settings.captureZone = effectiveCaptureZone(settings)
  scheduleSave()
  // Anything that changes the footprint or the threshold changes every score,
  // so the stale chart is dropped before the plot is redrawn, not after, and
  // a fresh one is scored once the panel has settled.
  const scoring: (keyof Settings)[] = [
    'signalCheckOn',
    'signalBwHz',
    'signalSf',
    'signalCr',
    'payloadBytes',
    'thresholdMode',
    'thresholdMarginDb',
    'thresholdAbsDb',
    // The filter is part of the scoring now: it decides which interference
    // this receiver would actually hear and which centers we can afford.
    'filterOn',
    'filterCenterHz',
    'filterBwHz',
    'filterOrder',
    'filterRejectionDb',
    'fitLossBudgetDb',
  ]
  if (scoring.some((key) => key in patch)) invalidateProfile()
  apply()
})
controls.render(settings)

// Chrome, not an option: collapsing a section changes nothing about the view,
// so it neither re-renders nor survives Reset options.
initSections($<HTMLFormElement>('options'), settings.collapsedSections, (ids) => {
  settings = { ...settings, collapsedSections: ids }
  scheduleSave()
})

// ------------------------------------------------------------------ schemes

const BUILTIN_SCHEMES = builtinSchemes()
let userSchemes: Scheme[] = loadUserSchemes()
const allSchemes = (): Scheme[] => [...BUILTIN_SCHEMES, ...userSchemes]

const schemePanel = new SchemePanel(
  {
    list: $('scheme-list'),
    addButton: $<HTMLButtonElement>('add-scheme'),
    dialog: $<HTMLDialogElement>('scheme-dialog'),
    json: $<HTMLTextAreaElement>('scheme-json'),
    error: $<HTMLElement>('scheme-error'),
  },
  {
    onVisibleChange: (ids) => {
      settings = { ...settings, visibleSchemes: ids }
      scheduleSave()
      // No re-render: the boxes already show the new state, and rebuilding
      // them would drop keyboard focus from the one just toggled.
      apply()
    },
    onAdd: (scheme) => {
      userSchemes = [...userSchemes, scheme]
      saveUserSchemes(userSchemes)
      // A scheme someone just typed in is one they want to see.
      settings = { ...settings, visibleSchemes: [...settings.visibleSchemes, scheme.id] }
      scheduleSave()
      renderSchemes()
      apply()
    },
    onRemove: (id) => {
      userSchemes = userSchemes.filter((s) => s.id !== id)
      saveUserSchemes(userSchemes)
      settings = { ...settings, visibleSchemes: settings.visibleSchemes.filter((v) => v !== id) }
      scheduleSave()
      renderSchemes()
      apply()
    },
  },
)

function renderSchemes(): void {
  schemePanel.render(allSchemes(), settings.visibleSchemes)
}
renderSchemes()

/** The schemes to draw, in panel order, each with its fixed color. */
function visibleSchemes(): VisibleScheme[] {
  const wanted = new Set(settings.visibleSchemes)
  return allSchemes()
    .filter((scheme) => wanted.has(scheme.id))
    .map((scheme) => ({ scheme, color: schemePanel.colorOf(scheme.id) }))
}

// ------------------------------------------------------------------- filter

const filterNote = $<HTMLParagraphElement>('filter-note')

/** The filter in force, or null. The one place the on/off switch is read. */
function activeFilter(): ResolvedFilter | null {
  const run = runs[currentRun]
  return run && settings.filterOn ? resolveFilter(run, settings.offsetHz, settings) : null
}

/**
 * What the filter is doing, and what it does to the search.
 *
 * The filter sits in the station's path, so it cuts both ways, and saying only
 * half of that would be worse than saying nothing: it decides which
 * interference this receiver would actually hear, and it decides what our own
 * transmission would pay to sit anywhere but the passband.
 */
function renderFilterNote(filter: ResolvedFilter | null): void {
  if (!filter || !settings.filterOn) {
    filterNote.textContent = ''
    return
  }
  // One bandwidth out from center: far enough to say something about the
  // skirt, close enough that the cap has usually not taken over yet.
  const out = attenuationDb(
    filter.centerHz + filter.bwHz,
    filter.centerHz,
    filter.bwHz,
    filter.order,
    filter.rejectionDb,
  )
  filterNote.textContent =
    `Passes ${formatHz(filter.lowHz)} – ${formatHz(filter.highHz)}, ` +
    `down ${out.toFixed(0)} dB one bandwidth out. The signal check scores what this receiver would hear, ` +
    `and says what your own signal would lose.`
}

// ------------------------------------------------------------ signal check

const signalShapeText = $<HTMLParagraphElement>('signal-shape')
const checkStatus = $<HTMLParagraphElement>('check-status')
const addPinButton = $<HTMLButtonElement>('add-pin')
const pinList = $<HTMLOListElement>('pin-list')

/** The pinned centers, in the order they were put down. Per capture, so they live in memory. */
let pins: PlacedSignal[] = []
/** Each pin's own metrics, by index; null where the center could not be scored. */
let pinScores: (CenterScore | null)[] = []
/** The scored chart, or null while the check is off or a fresh one is pending. */
let profile: CollisionProfile | null = null
let profileNote = ''

const checkOn = (): boolean => settings.signalCheckOn

/** What the viewer draws for the check: the chart so far and the footprint the column takes. */
function checkDisplay(): { profile: CollisionProfile | null; widthHz: number; airtimeS: number } | null {
  if (!checkOn() || !viewer.clip) return null
  const shape = signalShape(settings)
  return { profile, widthHz: shape.widthHz, airtimeS: shape.airtimeS }
}

/** The chart as the export draws it, or null when there is none yet. */
function checkOverlay(): CheckOverlay | null {
  const check = checkDisplay()
  if (!check?.profile) return null
  return {
    scores: check.profile.scores,
    bin0: check.profile.bin0,
    widthHz: check.widthHz,
    airtimeS: check.airtimeS,
    budget: check.profile.budget,
    peak: check.profile.peak,
  }
}

/** The pins as foci on the chart, for the export. */
function pinFoci(): CheckFocus[] {
  const clip = viewer.clip
  if (!clip || !checkOn()) return []
  const filter = activeFilter()
  const widthHz = signalShape(settings).widthHz
  return pins.map((pin) => ({
    centerHz: pin.centerHz,
    startMs: pin.startMs,
    score: profile ? scoreAt(profile, clip, pin.centerHz) : NaN,
    lossDb: filter ? insertionLossDb(pin.centerHz, widthHz, filter) : 0,
  }))
}

/** How close to a channel center a click must be to snap onto it. */
const SNAP_HZ = 10e3

/**
 * The nearest channel center among the overlays currently shown when within
 * SNAP_HZ of it, otherwise the frequency itself. Soft on purpose: a visible
 * scheme is a hint for landing exactly on one of its channels, not a
 * constraint against checking between them, and with nothing shown there is
 * nothing to snap to.
 */
function snapToVisible(hz: number): number {
  let best = hz
  let bestDistance = Infinity
  for (const { scheme } of visibleSchemes()) {
    for (const channel of scheme.channels) {
      const d = Math.abs(channel.centerHz - hz)
      if (d < bestDistance) {
        bestDistance = d
        best = channel.centerHz
      }
    }
  }
  return bestDistance <= SNAP_HZ ? best : hz
}

viewer.setSnap(snapToVisible)
viewer.onPin((hz, ms) => {
  pins = [...pins, { centerHz: hz, startMs: ms }]
  // One click, one pin: the mode ends with the pin it was turned on for,
  // rather than leaving a loaded pointer over a plot meant for reading.
  setPlacementMode(false)
  evaluatePins()
  apply()
})

function setPlacementMode(on: boolean): void {
  viewer.setPlacementMode(on)
  addPinButton.setAttribute('aria-pressed', String(on))
  // Asked at the moment it is used, not at startup: a tablet with a keyboard
  // attached can be either kind of device within one session.
  const act = matchMedia('(pointer: coarse)').matches ? 'Tap' : 'Click'
  addPinButton.textContent = on ? `${act} the plot…` : 'Add pin'
}

addPinButton.addEventListener('click', () => setPlacementMode(!viewer.placing))

function evaluatePins(): void {
  const clip = viewer.clip
  const shape = signalShape(settings)
  const filter = activeFilter()
  pinScores =
    clip && checkOn()
      ? pins.map((pin) => evaluateCenter(clip, pin.centerHz, shape.widthHz, shape.airtimeS, threshold(settings), filter))
      : pins.map(() => null)
  renderCheck()
}

function clearPins(): void {
  pins = []
  pinScores = []
  setPlacementMode(false)
}

/**
 * The chart describes a footprint, threshold and clip; any change to those
 * makes it stale. It is dropped at once and scored again after a short pause,
 * so a slider being dragged does not score on every frame of the drag.
 */
let scoreTimer = 0
function invalidateProfile(): void {
  profile = null
  profileNote = ''
  clearTimeout(scoreTimer)
  scoreTimer = 0
}

function scheduleScore(): void {
  if (scoreTimer || profile || !checkOn() || !viewer.clip) return
  checkStatus.textContent = 'Scoring…'
  scoreTimer = window.setTimeout(() => {
    scoreTimer = 0
    scoreNow()
  }, 60)
}

function scoreNow(): void {
  const clip = viewer.clip
  if (!clip || !checkOn()) return
  const shape = signalShape(settings)
  const filter = activeFilter()
  const started = performance.now()
  profile = collisionProfile({
    clip,
    widthHz: shape.widthHz,
    airtimeS: shape.airtimeS,
    threshold: threshold(settings),
    filter,
    lossBudgetDb: settings.fitLossBudgetDb,
  })
  const ms = Math.round(performance.now() - started)
  const budgeted = filter ? ` ${profile.inBudget} of them keep your signal within ${settings.fitLossBudgetDb} dB of the passband.` : ''
  profileNote =
    profile.evaluated === 0
      ? `Nothing to score: no center fits ${describeShape(shape)} inside the crop with data.`
      : `Scored ${profile.evaluated} centers over ${profile.presentRows} rows of data ` +
        `(${profile.windowRows} row window), ${ms} ms. Worst ${formatPercent(profile.peak)}.${budgeted}`
  evaluatePins()
  apply()
}

const signedDb = (db: number) => {
  const rounded = Math.abs(db) < 0.05 ? 0 : db
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)} dB`
}

function renderCheck(): void {
  const shape = signalShape(settings)
  signalShapeText.textContent = describeShape(shape)
  const run = runs[currentRun]
  const on = run !== undefined && checkOn()
  checkStatus.textContent = on ? profileNote : ''
  addPinButton.disabled = !on
  if (!on && viewer.placing) setPlacementMode(false)
  renderPins(run, on)
}

/**
 * The pins as rows, numbered to match the blocks on the plot. The row itself
 * pans to its pin, since a pin put down before a zoom is otherwise findable
 * only by reading frequencies off the tape.
 */
function renderPins(run: Run | undefined, on: boolean): void {
  pinList.replaceChildren()
  if (!run || !on) return
  const zone = displayZone(settings)
  pins.forEach((pin, index) => {
    // The row stays a list item so its marker carries the number the block on
    // the plot is labelled with; the flex row is inside it.
    const li = document.createElement('li')
    const row = document.createElement('div')
    row.className = 'pin-row'

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'pin-go'
    // Hour and minute only: the row has to fit a 250 px panel, and the full
    // instant is in the title. The score does not depend on it either way —
    // where the block sits in time is only where it was put down.
    button.textContent = `${formatMHz(pin.centerHz, run.geometry.stepHz)} · ${formatClock(pin.startMs, zone, 'hm')}`
    button.title = `Show pin ${index + 1} on the plot. Placed at ${formatClock(pin.startMs, zone, 'full')}.`
    button.addEventListener('click', () => viewer.revealHz(pin.centerHz))

    const detail = document.createElement('span')
    detail.className = 'pin-detail'
    const score = pinScores[index]
    // What the filter costs here is part of the answer, not a detail: a center
    // down the skirt is one a transmission would not get out of.
    const loss = score && score.filterLossDb > 0.05 ? `, filter −${score.filterLossDb.toFixed(1)} dB` : ''
    detail.textContent = score
      ? `busy ${formatPercent(score.score)}, occupancy ${formatPercent(score.occupancy)}, ` +
        `${signedDb(score.excessDb)} over floor${loss}`
      : 'no data at this center'
    button.append(detail)

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'pin-remove'
    remove.textContent = '×'
    remove.title = `Remove pin ${index + 1}`
    remove.setAttribute('aria-label', `Remove pin ${index + 1}`)
    remove.addEventListener('click', () => {
      pins = pins.filter((_, i) => i !== index)
      evaluatePins()
      apply()
    })

    row.append(button, remove)
    li.append(row)
    pinList.append(li)
  })
}

/**
 * Dragging a dB slider fires continuously; writing localStorage on every frame
 * of that is a synchronous store per event for a setting nobody reads until
 * the next session.
 */
let saveTimer = 0
function scheduleSave(): void {
  clearTimeout(saveTimer)
  saveTimer = window.setTimeout(() => saveSettings(settings), 200)
}
addEventListener('pagehide', () => saveSettings(settings))

// ---------------------------------------------------------- shareable URL

/** What the address bar should say about what is on screen right now. */
function currentViewState(): ViewState {
  // The window is left out when the whole clip is on screen, so an unzoomed
  // view shares as a short link rather than as four redundant numbers.
  const window = viewer.full ? null : viewer.window
  return {
    settings,
    run: currentRun,
    freq: window ? { lowHz: window.lowHz, highHz: window.highHz } : null,
    time: window ? { startMs: window.startMs, endMs: window.endMs } : null,
    pins,
  }
}

/**
 * `replaceState`, never `pushState`: a pan is not a navigation, and pushing
 * would turn the back button into an undo history of every wheel click.
 */
let urlTimer = 0
function scheduleUrl(): void {
  if (!sharedSource) return
  clearTimeout(urlTimer)
  urlTimer = window.setTimeout(() => {
    if (!sharedSource) return
    history.replaceState(null, '', `${location.pathname}${viewQuery(sharedSource, currentViewState())}${location.hash}`)
  }, 250)
}

/**
 * Drops the query once a local file is open. The capture on screen is one the
 * link cannot fetch, so leaving `?load=` in the bar would describe a view
 * nobody else could reach and this one would not survive a reload.
 */
function clearUrl(): void {
  sharedSource = null
  pendingView = null
  clearTimeout(urlTimer)
  if (location.search) history.replaceState(null, '', location.pathname + location.hash)
}

/** Applies a shared window and block, once, against the run it was shared from. */
function restoreSharedView(): void {
  const shared = pendingView
  pendingView = null
  if (!shared) return
  if (shared.pins.length > 0) {
    pins = [...shared.pins]
    evaluatePins()
  }
  const window = viewer.window
  if (!window || (!shared.freq && !shared.time)) return
  // Either half may be absent, in a hand-edited link as much as a shared one.
  viewer.showWindow({
    lowHz: shared.freq?.lowHz ?? window.lowHz,
    highHz: shared.freq?.highHz ?? window.highHz,
    startMs: shared.time?.startMs ?? window.startMs,
    endMs: shared.time?.endMs ?? window.endMs,
  })
}

viewer.onViewChange(scheduleUrl)

/** The options that decide which data a clip holds, as a comparable key. */
function clipKey(run: Run, s: typeof settings): string {
  return JSON.stringify([
    run.id,
    s.lowHz,
    s.highHz,
    s.begin,
    s.end,
    s.headSec,
    s.tailSec,
    s.compress,
    s.offsetHz,
    s.captureZone,
  ])
}
let lastClipKey: string | null = null

function apply(): void {
  const run = runs[currentRun]
  if (!run) {
    viewer.setDisplay({
      palette: settings.palette,
      displayZone: displayZone(settings),
      ytickSec: settings.ytickSec,
      dbMin: 0,
      dbMax: 1,
      schemes: [],
      check: null,
      pins: [],
      filter: null,
    })
    hintFrequencyRange(null)
    controls.setDbRange(null, settings)
    controls.setFilterRange(null, 0, 0)
    renderFilterNote(null)
    lastClipKey = null
    viewer.setClip(null)
    renderCheck()
    scheduleUrl()
    return
  }
  try {
    // The clip is resolved first: the display carries overlays measured
    // against it, so it has to be current before they are built.
    //
    // A color-only change keeps the clip and its pyramid, so dragging a dB
    // slider repaints from cached reductions instead of rebuilding them.
    const key = clipKey(run, settings)
    if (key !== lastClipKey || !viewer.clip) {
      const firstClip = lastClipKey === null
      lastClipKey = key
      // A new crop or time range is a new scope for the chart.
      if (!firstClip) invalidateProfile()
      viewer.setClip(buildClip(run, settings))
      if (pins.length > 0) evaluatePins()
    }
    const [dbMin, dbMax] = clampDbRange(run, settings.dbMin, settings.dbMax)
    const band = displayedBand(run, settings.offsetHz)
    const resolved = resolveFilter(run, settings.offsetHz, settings)
    viewer.setDisplay({
      palette: settings.palette,
      displayZone: displayZone(settings),
      ytickSec: settings.ytickSec,
      dbMin,
      dbMax,
      schemes: visibleSchemes(),
      check: checkDisplay(),
      pins: checkOn() ? pins : [],
      filter: settings.filterOn ? resolved : null,
    })
    renderCheck()
    // Scored after the display is up, so the plot never waits on the chart.
    scheduleScore()
    hintFrequencyRange(band)
    controls.setDbRange({ lo: run.minDb, hi: run.maxDb }, settings)
    controls.setFilterRange(
      { lowHz: band.lowHz, highHz: band.highHz, stepHz: run.geometry.stepHz },
      resolved.centerHz,
      resolved.bwHz,
    )
    renderFilterNote(resolved)
    scheduleUrl()
  } catch (error) {
    setStatus(`Cannot apply options: ${(error as Error).message}`)
  }
}

/** Puts the loaded band on the low/high fields and flags a crop outside it. */
function hintFrequencyRange(band: { lowHz: number; highHz: number } | null): void {
  if (!band) {
    controls.hint('lowHz', 'e.g. 902.5M', false, '')
    controls.hint('highHz', 'e.g. 915M', false, '')
    return
  }
  const span = `${formatHz(band.lowHz)} – ${formatHz(band.highHz)}`
  const outside = (hz: number | null) => hz !== null && (hz < band.lowHz || hz > band.highHz)
  const title = `This capture covers ${span}. Values outside it are clamped to the data.`
  controls.hint('lowHz', formatHz(band.lowHz), outside(settings.lowHz), title)
  controls.hint('highHz', formatHz(band.highHz), outside(settings.highHz), title)
}

function setStatus(text: string, warn = false): void {
  status.textContent = text
  status.title = text
  status.classList.toggle('warning', warn)
}

/**
 * The card in the middle of the plot. It is the only thing on screen before a
 * capture is open, so it says what to do; while one is loading it reports
 * progress in the same place rather than sending the eye to the bar.
 */
interface EmptyState {
  kind: 'idle' | 'loading' | 'error'
  title: string
  note: string
}

const IDLE_EMPTY: EmptyState = {
  kind: 'idle',
  title: 'Open an rtl_power capture',
  note: 'Or drop a .csv or .csv.gz file anywhere on this page.',
}
let emptyState = IDLE_EMPTY

function setEmptyState(state: EmptyState): void {
  emptyState = state
  renderEmpty()
}

function renderEmpty(): void {
  // Only when there is nothing to look at: a failed second load keeps the
  // capture already on screen, and reports itself in the status bar.
  empty.hidden = runs.length > 0
  emptyTitle.textContent = emptyState.title
  emptyNote.textContent = emptyState.note
  emptyOpen.hidden = emptyState.kind === 'loading'
  // Also the hook scripts/screenshot.mjs waits on.
  document.body.dataset.state = runs.length > 0 ? 'ready' : emptyState.kind
}

emptyOpen.addEventListener('click', () => fileInput.click())

function describeRun(run: Run): string {
  const lowHz = run.binHz[0]!
  const highHz = run.binHz[run.binCount - 1]! + run.geometry.stepHz
  return `${formatHz(lowHz)} – ${formatHz(highHz)}, ${run.binCount} bins, ${run.sweepCount} sweeps`
}

function showRuns(): void {
  runSelect.replaceChildren()
  for (const run of runs) {
    const option = document.createElement('option')
    option.value = String(run.id)
    option.textContent = `Run ${run.id + 1}: ${describeRun(run)}`
    runSelect.append(option)
  }
  runSelect.hidden = runs.length < 2
  runSelect.value = String(currentRun)

  const run = runs[currentRun]
  diagnosticsList.replaceChildren()
  if (run) {
    for (const note of run.diagnostics) {
      const li = document.createElement('li')
      li.textContent = note
      diagnosticsList.append(li)
    }
  }
  diagnostics.hidden = !run || run.diagnostics.length === 0
  exportButton.disabled = !run
  resetViewButton.disabled = !run
  renderEmpty()
}

// ------------------------------------------------------------------ loading

let worker: Worker | null = null

function load(file: File): void {
  worker?.terminate()
  worker = new LoadWorker()
  fileName = file.name
  setStatus(`Loading ${file.name}…`)
  setEmptyState({ kind: 'loading', title: `Loading ${file.name}…`, note: '' })
  exportButton.disabled = true

  const failed = (reason: string) => {
    setStatus(`Failed to load ${file.name}: ${reason}`)
    setEmptyState({ kind: 'error', title: `Could not read ${file.name}`, note: reason })
  }

  worker.onmessage = (event: MessageEvent<LoadMessage>) => {
    const message = event.data
    if (message.type === 'progress') {
      const pct = file.size > 0 ? Math.min(100, Math.round((message.bytesRead / file.size) * 100)) : 0
      const lines = `${message.lines.toLocaleString()} lines`
      setStatus(`Loading ${file.name}… ${pct}% (${lines})`)
      setEmptyState({ kind: 'loading', title: `Loading ${file.name}…`, note: `${pct}%, ${lines}` })
      return
    }
    if (message.type === 'error') {
      failed(message.message)
      return
    }
    runs = message.runs
    // The run comes from the link before the clip is built, so the shared
    // window is resolved against the run it was shared from.
    currentRun = pendingView && pendingView.run < message.runs.length ? pendingView.run : 0
    clearPins()
    invalidateProfile()
    if (runs.length === 0) {
      const detail = `${message.lines} lines read, ${message.badLines} unparseable${
        message.truncated ? `. The file ${message.truncated}` : ''
      }${message.samples[0] ? `. ${message.samples[0]}` : ''}`
      setStatus(`${file.name}: no sweeps found (${detail})`)
      setEmptyState({ kind: 'error', title: `No sweeps found in ${file.name}`, note: detail })
      showRuns()
      apply()
      return
    }
    const run = runs[0]!
    if (message.badLines > 0) run.diagnostics.push(...message.samples)
    // A file that stopped short still loaded. Saying so in the bar rather than
    // only in Diagnostics, because every reading after the cut is absent from
    // the plot and nothing else on screen would show that.
    const cut = message.truncated ? ` — incomplete: the file ${message.truncated}` : ''
    setStatus(`${file.name}: ${runs.length === 1 ? describeRun(run) : `${runs.length} runs`}${cut}`, cut !== '')
    setEmptyState(IDLE_EMPTY)
    showRuns()
    viewer.setClip(null)
    apply()
    restoreSharedView()
  }
  worker.onerror = (event) => failed(event.message)

  const request: LoadRequest = { type: 'load', file, timeZone: effectiveCaptureZone(settings) }
  worker.postMessage(request)
}

fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0]
  if (file) {
    clearUrl()
    load(file)
  }
  fileInput.value = ''
})

let dragDepth = 0
document.addEventListener('dragenter', (event) => {
  event.preventDefault()
  dragDepth++
  drop.hidden = false
})
document.addEventListener('dragover', (event) => event.preventDefault())
document.addEventListener('dragleave', () => {
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) drop.hidden = true
})
document.addEventListener('drop', (event) => {
  event.preventDefault()
  dragDepth = 0
  drop.hidden = true
  const file = event.dataTransfer?.files[0]
  if (file) {
    clearUrl()
    load(file)
  }
})

runSelect.addEventListener('change', () => {
  currentRun = Number(runSelect.value)
  clearPins()
  invalidateProfile()
  showRuns()
  viewer.setClip(null)
  apply()
})

// ------------------------------------------------------------------ chrome

resetViewButton.addEventListener('click', () => viewer.resetView())

/**
 * The options panel: a column beside the plot on a wide screen, a drawer over
 * it on a narrow one.
 *
 * `inert` rather than `hidden`, because a drawer has to stay rendered to slide;
 * without it the panel would still be reachable by keyboard while parked
 * off-screen.
 */
function setControlsOpen(open: boolean): void {
  controlsPanel.classList.toggle('closed', !open)
  controlsPanel.toggleAttribute('inert', !open)
  toggleControls.setAttribute('aria-expanded', String(open))
}

/** The width at which the panel becomes a drawer; matches the media query. */
const NARROW = '(max-width: 720px)'

// On a phone the drawer starts out of the way: the waterfall is what someone
// opened the page to look at, and it is the whole screen. Decided once, not on
// resize, so rotating a phone never moves the panel out from under a thumb.
setControlsOpen(!matchMedia(NARROW).matches)

toggleControls.addEventListener('click', () => setControlsOpen(controlsPanel.classList.contains('closed')))
$<HTMLButtonElement>('close-controls').addEventListener('click', () => setControlsOpen(false))

const aboutDialog = $<HTMLDialogElement>('about-dialog')
$<HTMLButtonElement>('about').addEventListener('click', () => aboutDialog.showModal())

$<HTMLButtonElement>('reset-options').addEventListener('click', () => {
  // Which sections are open is the panel's layout, not an option, so it
  // survives a reset along with the export width.
  const keep = { exportWidth: settings.exportWidth, collapsedSections: settings.collapsedSections }
  settings = { ...defaultSettings(), ...keep }
  saveSettings(settings)
  controls.render(settings)
  renderSchemes()
  invalidateProfile()
  apply()
})

// ------------------------------------------------------------------ export

const exportForm = exportDialog.querySelector('form')!
const exportWidthInput = exportForm.elements.namedItem('width') as HTMLInputElement

function refreshExportInfo(): void {
  const clip = viewer.clip
  if (!clip) return
  const width = Number(exportWidthInput.value) || DEFAULT_EXPORT_WIDTH
  const schemeCount = visibleSchemes().length
  const plan = planExport(clip, width, schemeCount)
  const strips = schemeCount > 0 ? `, ${schemeCount} scheme strip${schemeCount === 1 ? '' : 's'}` : ''
  exportInfo.textContent = `${plan.width} × ${plan.height} px: ${clip.bin1 - clip.bin0} bins across, ${plan.rows} rows of time${strips}.`
  const tooTall = plan.rows > MAX_EXPORT_ROWS
  exportWarning.hidden = !tooTall
  exportWarning.textContent = tooTall
    ? `${plan.rows} rows exceeds ${MAX_EXPORT_ROWS}. Set Compress or narrow the time range first.`
    : ''
  exportOk.disabled = tooTall
}

exportButton.addEventListener('click', () => {
  if (!viewer.clip) return
  exportWidthInput.value = String(settings.exportWidth || DEFAULT_EXPORT_WIDTH)
  refreshExportInfo()
  exportDialog.showModal()
})
exportWidthInput.addEventListener('input', refreshExportInfo)

exportDialog.addEventListener('close', () => {
  if (exportDialog.returnValue !== 'ok') return
  const clip = viewer.clip
  const pyramid = viewer.pyramid
  const run = runs[currentRun]
  if (!clip || !pyramid || !run) return
  const width = Math.max(16, Math.min(16384, Math.round(Number(exportWidthInput.value) || DEFAULT_EXPORT_WIDTH)))
  settings = { ...settings, exportWidth: width }
  saveSettings(settings)
  const [dbMin, dbMax] = clampDbRange(run, settings.dbMin, settings.dbMax)
  setStatus('Rendering PNG…')
  // Let the status paint before the synchronous render.
  requestAnimationFrame(() => {
    try {
      const canvas = renderExport(clip, pyramid, {
        width,
        palette: settings.palette,
        displayZone: displayZone(settings),
        ytickSec: settings.ytickSec,
        dbMin,
        dbMax,
        schemes: visibleSchemes(),
        check: checkOverlay(),
        pins: pinFoci(),
        filter: activeFilter(),
      })
      canvas.toBlob((blob) => {
        if (!blob) {
          setStatus('PNG export failed: the browser could not encode the canvas.')
          return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = exportFileName(clip, displayZone(settings))
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 10_000)
        setStatus(`${fileName}: exported ${canvas.width} × ${canvas.height} px`)
      }, 'image/png')
    } catch (error) {
      setStatus(`PNG export failed: ${(error as Error).message}`)
    }
  })
})

// Keyboard: Escape leaves placement mode, R resets the view, O toggles options.
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && viewer.placing) {
    setPlacementMode(false)
    return
  }
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return
  if (event.key === 'r') viewer.resetView()
  if (event.key === 'o') toggleControls.click()
})

showRuns()
apply()

if (import.meta.env.DEV) {
  // A handle for scripts/screenshot.mjs --eval checks; never in production builds.
  Object.assign(window, {
    __sps: {
      viewer,
      getSettings: () => settings,
      renderExport,
      buildClip,
      schemes: allSchemes,
      visibleSchemes,
      check: checkDisplay,
      pins: () => pins,
      profile: () => profile,
      filter: activeFilter,
    },
  })
}

// `?load=<url>` fetches a CSV from the same origin at startup, for sharing a
// link to a capture hosted next to the app and for automated checks. The rest
// of the query says what to do with it; see url.ts.
if (sharedSource) {
  const source = sharedSource
  setStatus(`Fetching ${source}…`)
  // Set before the fetch, so the page never shows "open a file" for an instant
  // while one is already on its way.
  setEmptyState({ kind: 'loading', title: `Fetching ${source}…`, note: '' })
  fetch(source)
    .then(async (response) => {
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
      const blob = await response.blob()
      load(new File([blob], source.split('/').pop() || 'capture.csv'))
    })
    .catch((error: Error) => {
      setStatus(`Failed to fetch ${source}: ${error.message}`)
      setEmptyState({ kind: 'error', title: `Could not fetch ${source}`, note: error.message })
    })
}
